// Real API client for the AI SDLC Pipeline Orchestrator (orchestrator/app/api/main.py).
// Base URL is configurable via VITE_ORCHESTRATOR_API_URL (Vite exposes
// import.meta.env.VITE_* vars at build/dev time); defaults to the
// orchestrator's local dev server port from its own README.

const BASE_URL = import.meta.env.VITE_ORCHESTRATOR_API_URL || 'http://localhost:8000'

class ApiError extends Error {
  constructor(message, status, body) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

async function request(path, options = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  })
  if (!res.ok) {
    let body = null
    try { body = await res.json() } catch { /* non-JSON error body */ }
    throw new ApiError(
      body?.detail || `${options.method || 'GET'} ${path} failed with ${res.status}`,
      res.status,
      body
    )
  }
  return res.json()
}

// GET /health
export function getHealth() {
  return request('/health')
}

// GET /feedback?status=&source=&limit=&offset=
export function listFeedback({ status, source, limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (source) params.set('source', source)
  params.set('limit', limit)
  params.set('offset', offset)
  return request(`/feedback?${params.toString()}`)
}

// GET /feedback/{id}
export function getFeedbackStatus(feedbackId) {
  return request(`/feedback/${feedbackId}`)
}

// GET /feedback/{id}/audit -- full step-by-step gate trail
export function getFeedbackAudit(feedbackId) {
  return request(`/feedback/${feedbackId}/audit`)
}

// POST /feedback
export function createFeedback({ rawFeedback, userId, userLocale = 'en' }) {
  return request('/feedback', {
    method: 'POST',
    body: JSON.stringify({ raw_feedback: rawFeedback, user_id: userId, user_locale: userLocale }),
  })
}

// POST /feedback/{id}/resume
export function resumeFeedback(feedbackId, { decision, comment, decidedBy }) {
  return request(`/feedback/${feedbackId}/resume`, {
    method: 'POST',
    body: JSON.stringify({ decision, comment, decided_by: decidedBy }),
  })
}

// POST /admin/mbo-ingest/run-once
export function triggerMboIngestOnce() {
  return request('/admin/mbo-ingest/run-once', { method: 'POST' })
}

// --- AI Analysis review (the "AI Analyses" view) ---

// GET /admin/mbo/analyses -- every Analysis in essms-mbo, newest first,
// annotated with any audit decision recorded here (auditDecision).
export function listMboAnalyses() {
  return request('/admin/mbo/analyses')
}

// POST /admin/mbo/analysis/{id}/decision -- accept/reject with optional
// voice-note (base64 MediaRecorder output).
export function decideAnalysis(analysisId, { decision, comment, decidedBy, audioBase64, audioMime }) {
  return request(`/admin/mbo/analysis/${analysisId}/decision`, {
    method: 'POST',
    body: JSON.stringify({
      decision,
      comment: comment || null,
      decided_by: decidedBy || null,
      audio_base64: audioBase64 || null,
      audio_mime: audioMime || null,
    }),
  })
}

// URL for playing back the voice note recorded with the latest decision.
export function decisionAudioUrl(analysisId) {
  return `${BASE_URL}/admin/mbo/analysis/${analysisId}/decision-audio`
}

// POST /admin/mbo/reassess/run -- batch AI re-assessment of every Issue
// (and optionally Requirement) in essms-mbo.
export function triggerReassessRun({ includeRequirements = true, force = false } = {}) {
  return request('/admin/mbo/reassess/run', {
    method: 'POST',
    body: JSON.stringify({ include_requirements: includeRequirements, force }),
  })
}

// GET /admin/mbo/reassess/status
export function getReassessStatus() {
  return request('/admin/mbo/reassess/status')
}

// POST /admin/mbo/reassess/item -- the per-item Challenge flow: re-assesses ONE
// analysis with the reviewer's challenge text fed into the fresh pass. Runs as a
// background task on the orchestrator; poll getReassessItemStatus until state != running.
export function triggerReassessItem({ scopeId, challenge }) {
  return request('/admin/mbo/reassess/item', {
    method: 'POST',
    body: JSON.stringify({ scope_id: scopeId, challenge }),
  })
}

// GET /admin/mbo/reassess/item/status
export function getReassessItemStatus() {
  return request('/admin/mbo/reassess/item/status')
}

// GET /admin/mbo/analytics/challenges -- how often AI suggestions get challenged
// (totalAnalyses/totalChallenged/challengeRatePct/byChallenger/byWeek).
export function getChallengeAnalytics() {
  return request('/admin/mbo/analytics/challenges')
}

export { ApiError }
